/**
 * 종목 메타데이터 관리 Pinia Store
 * 종목별 메타데이터(전일종가, 거래시간 등)를 중앙에서 관리
 */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
export const useMetadataStore = defineStore('metadata', () => {
    // === 상태 ===
    const symbolMetadata = ref(new Map());
    const isInitialized = ref(false);
    const loading = ref(false);
    const error = ref(null);
    // === 계산된 속성 ===
    /**
     * 메타데이터 개수
     */
    const metadataCount = computed(() => symbolMetadata.value.size);
    /**
     * 메타데이터가 비어있는지 확인
     */
    const isEmpty = computed(() => symbolMetadata.value.size === 0);
    /**
     * stockCd로 메타데이터 조회
     * @param stockCd - 종목 코드
     * @returns 종목 메타데이터 또는 undefined
     */
    const getMetadata = (stockCd) => {
        return symbolMetadata.value.get(stockCd);
    };
    /**
     * 여러 종목의 메타데이터 조회
     * @param stockCds - 종목 코드 배열
     * @returns 종목 메타데이터 배열
     */
    const getMetadataList = (stockCds) => {
        return stockCds
            .map((stockCd) => symbolMetadata.value.get(stockCd))
            .filter(Boolean);
    };
    /**
     * 모든 메타데이터 조회
     * @returns 모든 종목 메타데이터 배열
     */
    const getAllMetadata = () => {
        return Array.from(symbolMetadata.value.values());
    };
    /**
     * 메타데이터 설정
     * @param stockCd - 종목 코드
     * @param metadata - 메타데이터
     */
    const setMetadata = (stockCd, metadata) => {
        const newMetadata = new Map(symbolMetadata.value);
        newMetadata.set(stockCd, metadata);
        symbolMetadata.value = newMetadata;
    };
    /**
     * 메타데이터 일괄 설정
     * @param metadataList - 메타데이터 배열
     */
    const setMetadataList = (metadataList) => {
        const newMetadata = new Map(symbolMetadata.value);
        metadataList.forEach((metadata) => {
            newMetadata.set(metadata.stockCd, metadata);
        });
        symbolMetadata.value = newMetadata;
    };
    /**
     * 외부에서 전달된 OrderStocks 응답으로 메타데이터 초기화
     * @param metadataList - 종목 메타데이터 배열
     */
    const initializeMetadata = (metadataList) => {
        loading.value = true;
        error.value = null;
        try {
            const newMetadata = new Map();
            metadataList.forEach((metadata) => {
                newMetadata.set(metadata.stockCd, metadata);
            });
            symbolMetadata.value = newMetadata;
            isInitialized.value = true;
        }
        catch (err) {
            error.value = err.message;
            console.error('[MetadataStore] 메타데이터 초기화 실패:', err);
            throw err;
        }
        finally {
            loading.value = false;
        }
    };
    /**
     * 메타데이터 리셋
     */
    const resetMetadata = () => {
        symbolMetadata.value = new Map();
        isInitialized.value = false;
        loading.value = false;
        error.value = null;
    };
    /**
     * 메타데이터 존재 여부 확인
     * @param stockCd - 종목 코드
     * @returns 메타데이터 존재 여부
     */
    const hasMetadata = (stockCd) => {
        return symbolMetadata.value.has(stockCd);
    };
    /**
     * 전일 종가 업데이트
     * MarketTicker 실시간 데이터에서 beforeClose를 받아 전일 종가를 업데이트합니다.
     * 값이 실제로 변경된 경우에만 업데이트하여 불필요한 리렌더링 방지
     * @param stockCd - 종목 코드
     * @param closePrice - 전일 종가 (beforeClose)
     */
    const updateClosePrice = (stockCd, closePrice) => {
        const existingMetadata = symbolMetadata.value.get(stockCd);
        if (existingMetadata) {
            // 값이 실제로 변경된 경우에만 업데이트
            if (existingMetadata.closePrice === closePrice) {
                return;
            }
            const updatedMetadata = {
                ...existingMetadata,
                closePrice,
            };
            setMetadata(stockCd, updatedMetadata);
        }
    };
    return {
        // 상태
        symbolMetadata,
        isInitialized,
        loading,
        error,
        // 계산된 속성
        metadataCount,
        isEmpty,
        // 액션
        getMetadata,
        getMetadataList,
        getAllMetadata,
        setMetadata,
        setMetadataList,
        initializeMetadata,
        resetMetadata,
        hasMetadata,
        updateClosePrice,
    };
});
//# sourceMappingURL=useMetadataStore.js.map